import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Participant, Role, ServiceCenter} from "../../../../../types/domain";
import {MantineReactTable, MRT_ColumnDef, useMantineReactTable} from "mantine-react-table";
import {ActionIcon, Alert, Box, Button, Paper, Tooltip} from "@mantine/core";
import ParticipantService from "../../../../../services/participant.service";
import {ParticipantModal} from "../modal/ParticipantModal";
import {ModalType} from "../../../../../types/UITypes";
import CustomModal from "../modal/CustomModal";
import {IconArchive, IconPencil} from "@tabler/icons-react";
import {MRT_Localization_DE} from "mantine-react-table/locales/de";
import * as Sentry from "@sentry/react";

type UserData = {
    userRole: Role | null,
    userServiceCenters: Array<ServiceCenter>
}
type ParticipantTableProps = {
    userData: UserData,
    tabValue: string
}

export function ParticipantTable({userData: { userRole, userServiceCenters }, tabValue }: ParticipantTableProps) {
    const [data, setData] = useState<Participant[]>([]);
    const [showParticipantModal, setShowParticipantModal] = useState<boolean>(false);
    const [participantToEdit, setParticipantToEdit] = useState<Participant | null>(null);
    const [showAlert, setShowAlert] = useState(false);
    const [modal, setModal] = useState<ModalType>({
        show: false,
        title: "",
        body: "",
        decline: false,
        handleOk: (() => {}),
        handleCancel: (() => {})
    })

    //definition of table columns
    const columns = useMemo<MRT_ColumnDef<Participant>[]>(
        () => [
            {
                accessorKey: 'firstname',
                header: 'Vorname'
            },
            {
                accessorKey: 'lastname',
                header: 'Nachname'
            },
            {
                accessorKey: 'birthDate',
                header: 'Geburtsdatum',
                Cell: props => {
                    if (props.row.original.birthDate) {
                        const date = new Date(props.row.original.birthDate);
                        const formatter = new Intl.DateTimeFormat('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                        });
                        return formatter.format(date);
                    }
                }
            },
            {
                accessorKey: 'stage',
                header: 'Stadium',
                accessorFn: (row) => row.stage?.name,
            },
            {
                id: 'servicecenter',
                header: 'Servicestelle',
                accessorFn: (row) => row.serviceCenter?.name ?? row.group?.serviceCenter?.name,
            }

        ], [tabValue]
    );

    const fetchData = useCallback(async () => {
        if (tabValue !== "participants" || !userRole) return;
        try {
            const response = await ParticipantService.getAll();
            const activeParticipants = response.data.filter(p => p.active);

            if (userRole?.name === 'Admin') {
                setData(activeParticipants);
            } else if (userRole?.name === 'DSS Leiter*in') {
                setData(activeParticipants.filter(p =>
                    userServiceCenters.some(sc =>
                        sc.id === (p.serviceCenter?.id ?? p.group?.serviceCenter?.id)
                    )
                ));
            }
        } catch (e) {
            console.error(e);
        }
    }, [userRole, userServiceCenters, tabValue]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    /**
     * Closes participant modal and fetches data
     */
    const handleCloseParticipantModal = useCallback(async () => {
        setShowParticipantModal(false);
        setParticipantToEdit(null);
        await fetchData();
    }, [fetchData]);

    /**
     * Sets a participant to inactive in the DB
     * @param participant to inactivate
     */
    async function inactivateParticipant(participant: Participant) {
        setModal({
            ...modal,
            show: true,
            title: "Hinweis",
            body: "Soll der Eintrag wirklich archiviert werden?",
            decline: true,
            handleOk: async () => {
                participant.active = false;
                participant.group = null;
                await ParticipantService.update(participant, participant.id)
                    .catch((e: Error) => {
                        Sentry.captureException(e);
                        setShowAlert(true);
                        return;
                    });

                setModal({...modal, show: false})
                fetchData();
            },
            handleCancel: () => {
                setModal({...modal, show: false})
                fetchData();
            }
        })
    }

    /**
     * Opens a modal to update a participant
     * @param participant with new data to update
     */
    function updateParticipant(participant: Participant) {
        setParticipantToEdit(participant);
        setShowParticipantModal(true);
    }

    /**
     * Opens a modal to create a new participant
     */
    function createParticipant() {
        setParticipantToEdit(null);
        setShowParticipantModal(true);

    }

    const table = useMantineReactTable<Participant>({
        columns,
        data,
        initialState: {
            pagination: {
                pageSize: 10,
                pageIndex: 0
            },
            sorting: [
                {
                    id: 'firstname',
                    desc: false
                },
                {
                    id: 'lastname',
                    desc: false
                }
            ]
        },
        enableColumnActions: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        positionPagination: "bottom",
        enableRowActions: true,
        positionActionsColumn: "last",
        renderRowActions: ({row}) => {
            return <Box sx={{display: 'flex', flexWrap: 'nowrap', gap: '8px'}}>
                <Tooltip label="Bearbeiten">
                    <ActionIcon color="blueish" onClick={() => updateParticipant(row.original)}>
                        <IconPencil/>
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Archivieren">
                    <ActionIcon style={{color: "var(--salmon)"}} onClick={() => inactivateParticipant(row.original)}>
                        <IconArchive/>
                    </ActionIcon>
                </Tooltip>
            </Box>
        },
        localization: MRT_Localization_DE,
        mantinePaginationProps: {
            showRowsPerPage: false
        }
    });
    return (
        <>
            <Paper radius="xs" style={{marginTop: "1rem"}}>
                <MantineReactTable
                    table={table}
                />
                {
                    showAlert ?
                        <Alert id={'participantDatabaseAlert'} w={"100%"}
                               title={"Archivieren fehlgeschlagen"}
                               ta={"left"} color="red" mt={"10px"}>
                            Fehler beim Archivieren des Eintrages!
                        </Alert> : <></>
                }
                <div className="buttonRow">
                    <Button onClick={createParticipant}>
                        Neuer Eintrag
                    </Button>
                </div>
            </Paper>
            {
                showParticipantModal && (
                    <ParticipantModal
                        opened={showParticipantModal}
                        close={handleCloseParticipantModal}
                        title={participantToEdit ? "TeilnehmerIn aktualisieren" : "TeilnehmerIn anlegen"}
                        participant={participantToEdit}
                        userRole={userRole}
                        userServiceCenters={userServiceCenters}
                    />
                )
            }
            <CustomModal title={modal.title}
                         body={modal.body}
                         decline={modal.decline} showModal={modal.show} handleOk={modal.handleOk}
                         handleCancel={modal.handleCancel}
            />
        </>
    );
}